import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Table, Spinner, Dropdown, Form, Badge } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const fmtNum = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '0';
  return n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const AturanJual = () => {
  const navigate = useNavigate();
  const apiUrl = getApiUrl();

  // State User & Jam
  const [username, setUsername] = useState('Administrator');
  const [time, setTime] = useState({ date: '', clock: '' });

  // State Search & Obat
  const [txtkdobat, setTxtkdobat] = useState('');
  const [obatInfo, setObatInfo] = useState({
    kdbr: '',
    nama: '',
    satuan: '',
    satuanbeli: '',
    harga: 0,
    xisiobat: 1,
    disc: 0,
    harga_jadi: 0
  });
  const [rulesList, setRulesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ type: 'info', text: 'Silakan pilih atau cari obat untuk melihat dan mengatur aturan penjualan.' });

  // State Pagination Tabel Utama
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterKlasifikasi, setFilterKlasifikasi] = useState('');

  // State Form Edit Baris Aktif
  const [selectedRule, setSelectedRule] = useState(null);
  const [formRow, setFormRow] = useState({
    kd_klasifikasi: '',
    nm_klasifikasi: '',
    selisih: 'Tidak',
    titip: 'Tidak',
    khusus: 'Tidak',
    jual: 'Tidak',
    acuanhrg: 'Sesuai',
    hna: 0,
    disc: 0,
    harga: 0,
    hna2: 0,
    hda: 0,
    hnapaket: 0,
    hda2: 0,
    laba: 0,
    laba2: 0
  });
  const [isSaving, setIsSaving] = useState(false);

  // Modal State Pencarian Obat
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Modal Laporan & Modul Tambahan
  const [modalType, setModalType] = useState(null); // 'laba_minus' | 'titip' | 'khusus' | 'ditutup' | 'pembagian'
  const [modalTitle, setModalTitle] = useState('');
  const [modalData, setModalData] = useState([]);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [modalPageSize, setModalPageSize] = useState(10);
  const [modalSearch, setModalSearch] = useState('');

  // Update Clock
  useEffect(() => {
    const userSaved = localStorage.getItem('nama_user') || localStorage.getItem('user') || 'Administrator';
    setUsername(userSaved);

    const updateTime = () => {
      const d = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      setTime({
        date: d.toLocaleDateString('id-ID', options),
        clock: d.toLocaleTimeString('id-ID')
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Detail Obat & Rules
  const fetchObatRules = useCallback(async (kdbrTarget) => {
    const kode = (kdbrTarget || txtkdobat || '').trim();
    if (!kode) return;
    setIsLoading(true);
    setStatusMsg({ type: 'info', text: `Memuat data aturan penjualan obat ${kode}...` });

    try {
      const res = await fetch(`${apiUrl}/aturanjual_info_obat?kdbr=${encodeURIComponent(kode)}`);
      const json = await res.json();
      if (json.status === 1 && json.obat) {
        setObatInfo(json.obat);
        setRulesList(json.rules || []);
        setTxtkdobat(json.obat.kdbr);
        setCurrentPage(1);

        // Auto select first rule if available
        if (json.rules && json.rules.length > 0) {
          const r = json.rules[0];
          setSelectedRule(r.kd_klasifikasi);
          const hnaVal = parseFloat(json.obat?.harga || r.hna || 0);
          const discVal = parseFloat(r.disc || 0);
          const hargaVal = parseFloat(r.harga || (hnaVal - discVal));
          const hna2Val = parseFloat(r.hna2 || 0);
          const hnapaketVal = parseFloat(r.hnapaket || 0);
          const hdaVal = parseFloat(r.hda || Math.round(hna2Val * 1.11));
          const hda2Val = parseFloat(r.hda2 || Math.round(hnapaketVal * 1.11));
          const labaVal = parseFloat(r.laba || (hna2Val - hargaVal));
          const laba2Val = parseFloat(r.laba2 || (hnapaketVal - hargaVal));

          setFormRow({
            kd_klasifikasi: r.kd_klasifikasi,
            nm_klasifikasi: r.nm_klasifikasi,
            selisih: r.selisih || 'Tidak',
            titip: r.titip || 'Tidak',
            khusus: r.khusus || 'Tidak',
            jual: r.jual || 'Tidak',
            acuanhrg: r.acuanhrg || 'Sesuai',
            hna: hnaVal,
            disc: discVal,
            harga: hargaVal,
            hna2: hna2Val,
            hda: hdaVal,
            hnapaket: hnapaketVal,
            hda2: hda2Val,
            laba: labaVal,
            laba2: laba2Val
          });
        }

        setStatusMsg({ type: 'success', text: `Data obat ${json.obat.nama} (${json.obat.kdbr}) berhasil dimuat. Total ${json.rules?.length || 0} klasifikasi tarif.` });
      } else {
        setStatusMsg({ type: 'danger', text: json.msg || 'Data obat tidak ditemukan.' });
        setRulesList([]);
      }
    } catch (err) {
      console.error(err);
      setStatusMsg({ type: 'danger', text: `Gagal memuat obat: ${err.message}` });
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, txtkdobat]);

  // Load default on start
  useEffect(() => {
    // Default awal kode kosong, menunggu input atau pencarian obat dari user
  }, []);

  // Select Rule row to edit
  const selectRule = (r, curObat = obatInfo) => {
    setSelectedRule(r.kd_klasifikasi);
    const hnaVal = parseFloat(curObat?.harga || r.hna || 0);
    const discVal = parseFloat(r.disc || 0);
    const hargaVal = parseFloat(r.harga || (hnaVal - discVal));
    const hna2Val = parseFloat(r.hna2 || 0);
    const hnapaketVal = parseFloat(r.hnapaket || 0);
    const hdaVal = parseFloat(r.hda || Math.round(hna2Val * 1.11));
    const hda2Val = parseFloat(r.hda2 || Math.round(hnapaketVal * 1.11));
    const labaVal = parseFloat(r.laba || (hna2Val - hargaVal));
    const laba2Val = parseFloat(r.laba2 || (hnapaketVal - hargaVal));

    setFormRow({
      kd_klasifikasi: r.kd_klasifikasi,
      nm_klasifikasi: r.nm_klasifikasi,
      selisih: r.selisih || 'Tidak',
      titip: r.titip || 'Tidak',
      khusus: r.khusus || 'Tidak',
      jual: r.jual || 'Tidak',
      acuanhrg: r.acuanhrg || 'Sesuai',
      hna: hnaVal,
      disc: discVal,
      harga: hargaVal,
      hna2: hna2Val,
      hda: hdaVal,
      hnapaket: hnapaketVal,
      hda2: hda2Val,
      laba: labaVal,
      laba2: laba2Val
    });
  };

  const resetFormRow = () => {
    setSelectedRule(null);
    setFormRow({
      kd_klasifikasi: '',
      nm_klasifikasi: '',
      selisih: 'Tidak',
      titip: 'Tidak',
      khusus: 'Tidak',
      jual: 'Tidak',
      acuanhrg: 'Sesuai',
      hna: 0,
      disc: 0,
      harga: 0,
      hna2: 0,
      hda: 0,
      hnapaket: 0,
      hda2: 0,
      laba: 0,
      laba2: 0
    });
  };

  // Kalkulasi saat perubahan input form edit
  const handleFormChange = (field, val) => {
    setFormRow((prev) => {
      const updated = { ...prev, [field]: val };

      if (field === 'acuanhrg') {
        if (val === 'Sesuai') {
          updated.disc = 0;
          updated.harga = updated.hna;
          updated.hna2 = updated.hna;
          updated.hnapaket = updated.hna;
          updated.hda = Math.round(updated.hna * 1.11);
          updated.hda2 = Math.round(updated.hna * 1.11);
          updated.laba = 0;
          updated.laba2 = 0;
        }
      } else if (field === 'disc') {
        const d = parseFloat(val) || 0;
        updated.harga = Math.max(0, updated.hna - d);
        if (updated.acuanhrg === 'Sesuai') {
          updated.hna2 = updated.harga;
          updated.hnapaket = updated.harga;
          updated.hda = Math.round(updated.hna2 * 1.11);
          updated.hda2 = Math.round(updated.hnapaket * 1.11);
          updated.laba = 0;
          updated.laba2 = 0;
        } else {
          updated.laba = updated.hna2 - updated.harga;
          updated.laba2 = updated.hnapaket - updated.harga;
        }
      } else if (field === 'hna2') {
        const h2 = parseFloat(val) || 0;
        updated.hda = Math.round(h2 * 1.11);
        updated.laba = h2 - updated.harga;
      } else if (field === 'hnapaket') {
        const hp = parseFloat(val) || 0;
        updated.hda2 = Math.round(hp * 1.11);
        updated.laba2 = hp - updated.harga;
      }

      return updated;
    });
  };

  // Simpan Aturan Baris Aktif
  const handleSimpanAturan = async (customPayload = null) => {
    const payload = customPayload || {
      kd_obat1: obatInfo.kdbr,
      kd_klasifikasi: formRow.kd_klasifikasi,
      nm_klasifikasi: formRow.nm_klasifikasi,
      nm_obat: obatInfo.nama,
      satuan: obatInfo.satuan,
      selisih: formRow.selisih,
      titip: formRow.titip,
      khusus: formRow.khusus,
      jual: formRow.jual,
      acuanhrg: formRow.acuanhrg,
      hna: formRow.hna,
      disc: formRow.disc,
      harga: formRow.harga,
      hna2: formRow.hna2,
      hda: formRow.hda,
      hnapaket: formRow.hnapaket,
      hda2: formRow.hda2,
      laba: formRow.laba,
      laba2: formRow.laba2
    };

    if (!payload.kd_obat1 || !payload.kd_klasifikasi) {
      alert('Pilih salah satu baris klasifikasi tarif terlebih dahulu.');
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(`${apiUrl}/aturanjual_simpan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (json.status === 1) {
        setStatusMsg({ type: 'success', text: `Aturan untuk [${payload.kd_klasifikasi}] ${payload.nm_klasifikasi} berhasil disimpan!` });

        // Update local rulesList
        setRulesList((prev) =>
          prev.map((item) =>
            item.kd_klasifikasi === payload.kd_klasifikasi
              ? {
                  ...item,
                  selisih: payload.selisih,
                  titip: payload.titip,
                  khusus: payload.khusus,
                  jual: payload.jual,
                  acuanhrg: payload.acuanhrg,
                  hna: payload.hna,
                  disc: payload.disc,
                  harga: payload.harga,
                  hna2: payload.hna2,
                  hda: payload.hda,
                  hnapaket: payload.hnapaket,
                  hda2: payload.hda2,
                  laba: payload.laba,
                  laba2: payload.laba2
                }
              : item
          )
        );
      } else {
        alert(json.msg || 'Gagal menyimpan aturan.');
      }
    } catch (err) {
      alert(`Error simpan aturan: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Simpan Batch Sesuai untuk Semua Klasifikasi
  const handleSetSemuaSesuai = async () => {
    if (!obatInfo.kdbr) return;
    if (!window.confirm(`Yakin ingin menyetel SEMUA klasifikasi obat ${obatInfo.nama} menjadi acuan 'Sesuai'?`)) return;

    setIsSaving(true);
    try {
      const res = await fetch(`${apiUrl}/aturanjual_simpan_sesuai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kd_obat1: obatInfo.kdbr })
      });
      const json = await res.json();
      if (json.status === 1) {
        setStatusMsg({ type: 'success', text: json.msg });
        fetchObatRules(obatInfo.kdbr);
      } else {
        alert(json.msg || 'Gagal menyetel acuan sesuai.');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Pencarian Obat di TB001
  const handleSearchObat = async (keyword) => {
    setIsSearching(true);
    try {
      const res = await fetch(`${apiUrl}/aturanjual_cari_obat?keyword=${encodeURIComponent(keyword || '')}`);
      const json = await res.json();
      if (json.status === 1) {
        setSearchResults(json.data || []);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error(err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePilihObatModal = (item) => {
    setShowSearchModal(false);
    setTxtkdobat(item.kdbr);
    fetchObatRules(item.kdbr);
  };

  // Handler Buka Modal Laporan
  const handleOpenModal = async (type) => {
    setModalType(type);
    setIsLoadingModal(true);
    setModalData([]);
    setModalPage(1);
    setModalSearch('');

    let url = '';
    if (type === 'laba_minus') {
      setModalTitle('Laporan Aturan Penjualan - Laba Minus');
      url = `${apiUrl}/aturanjual_laba_minus`;
    } else if (type === 'titip') {
      setModalTitle('Daftar Aturan Penjualan - Obat Titipan');
      url = `${apiUrl}/aturanjual_daftar_titip`;
    } else if (type === 'khusus') {
      setModalTitle('Daftar Aturan Penjualan - Obat Khusus');
      url = `${apiUrl}/aturanjual_daftar_khusus`;
    } else if (type === 'ditutup') {
      setModalTitle('Daftar Stok Ada Tetapi Aturan Jual Ditutup');
      url = `${apiUrl}/aturanjual_setting_ditutup`;
    } else if (type === 'pembagian') {
      setModalTitle('Pengaturan Pembagian Bagi Hasil Obat Khusus');
      url = `${apiUrl}/aturanjual_pembagian_khusus`;
    }

    try {
      const res = await fetch(url);
      const json = await res.json();
      if (json.status === 1) {
        setModalData(json.data || []);
      } else {
        alert(json.msg || 'Gagal memuat data laporan.');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsLoadingModal(false);
    }
  };

  // Simpan Pembagian Obat Khusus
  const handleSavePembagian = async () => {
    try {
      const res = await fetch(`${apiUrl}/aturanjual_pembagian_khusus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: modalData })
      });
      const json = await res.json();
      if (json.status === 1) {
        alert('Persentase pembagian obat khusus berhasil disimpan!');
        setModalType(null);
      } else {
        alert(json.msg || 'Gagal menyimpan pembagian.');
      }
    } catch (err) {
      alert(`Error simpan pembagian: ${err.message}`);
    }
  };

  // Bersihkan Data Form & Input
  const handleBersih = () => {
    setTxtkdobat('');
    setObatInfo({
      kdbr: '',
      nama: '',
      satuan: '',
      satuanbeli: '',
      harga: 0,
      xisiobat: 1,
      disc: 0,
      harga_jadi: 0
    });
    setRulesList([]);
    resetFormRow();
    setCurrentPage(1);
    setStatusMsg({ type: 'info', text: 'Formulir dibersihkan. Silakan input kode atau nama obat baru.' });
  };

  // ============================================================
  // PAGINATION LOGIC - MAIN TABLE
  // ============================================================
  const filteredRules = useMemo(() => {
    if (!filterKlasifikasi.trim()) return rulesList;
    const q = filterKlasifikasi.toLowerCase().trim();
    return rulesList.filter(
      (r) =>
        (r.kd_klasifikasi || '').toLowerCase().includes(q) ||
        (r.nm_klasifikasi || '').toLowerCase().includes(q)
    );
  }, [rulesList, filterKlasifikasi]);

  const totalPages = useMemo(() => {
    if (pageSize === 'all') return 1;
    const sz = parseInt(pageSize, 10) || 10;
    return Math.max(1, Math.ceil(filteredRules.length / sz));
  }, [filteredRules.length, pageSize]);

  const paginatedRules = useMemo(() => {
    if (pageSize === 'all') return filteredRules;
    const sz = parseInt(pageSize, 10) || 10;
    const start = (currentPage - 1) * sz;
    return filteredRules.slice(start, start + sz);
  }, [filteredRules, currentPage, pageSize]);

  // Adjust page if out of bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // ============================================================
  // PAGINATION LOGIC - MODAL REPORT
  // ============================================================
  const filteredModalData = useMemo(() => {
    if (!modalSearch.trim()) return modalData;
    const q = modalSearch.toLowerCase().trim();
    return modalData.filter((item) => {
      const kd = (item.kd_obat1 || item.kd_obat || item.kd_klasifikasi || '').toLowerCase();
      const nm = (item.nm_obat || item.nm_klasifikasi || item.nm_prinsip || '').toLowerCase();
      return kd.includes(q) || nm.includes(q);
    });
  }, [modalData, modalSearch]);

  const totalModalPages = useMemo(() => {
    if (modalPageSize === 'all') return 1;
    const sz = parseInt(modalPageSize, 10) || 10;
    return Math.max(1, Math.ceil(filteredModalData.length / sz));
  }, [filteredModalData.length, modalPageSize]);

  const paginatedModalData = useMemo(() => {
    if (modalPageSize === 'all') return filteredModalData;
    const sz = parseInt(modalPageSize, 10) || 10;
    const start = (modalPage - 1) * sz;
    return filteredModalData.slice(start, start + sz);
  }, [filteredModalData, modalPage, modalPageSize]);

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 1. HEADER UTAMA */}
      <div
        style={{
          background: 'linear-gradient(90deg, #0f2b48 0%, #1e3a8a 100%)',
          color: '#ffffff',
          padding: '10px 18px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <i className="bi bi-gear-wide-connected" style={{ fontSize: '1.4rem', color: '#38bdf8' }}></i>
          <div>
            <div style={{ fontSize: '1.05rem', fontWeight: 800, letterSpacing: '0.5px' }}>
              ATURAN PENJUALAN OBAT SETIAP KLASIFIKASI
            </div>
            <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>
              Sistem Standar Harga Jual &amp; Margin Obat Berdasarkan Klasifikasi Tarif Pasien
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right', fontSize: '0.75rem' }}>
            <div style={{ fontWeight: 700 }}>{time.clock} <span style={{ opacity: 0.8, fontWeight: 500 }}>&bull; {time.date}</span></div>
            <div style={{ opacity: 0.85, fontSize: '0.7rem' }}>User: <b>{username}</b></div>
          </div>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate('/home')}
            style={{ fontWeight: 700, borderRadius: 7, fontSize: '0.75rem', padding: '5px 12px' }}
          >
            <i className="bi bi-house-door me-1" /> Home
          </Button>
        </div>
      </div>

      {/* 2. TOP BAR INFORMASI OBAT */}
      <div
        style={{
          background: '#0d233a',
          padding: '8px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          borderBottom: '2px solid #0284c7'
        }}
      >
        <span
          style={{
            background: '#0284c7',
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.75rem',
            padding: '4px 10px',
            borderRadius: 5,
            letterSpacing: '0.5px'
          }}
        >
          BARANG | SATUAN BELI | SATUAN JUAL
        </span>

        {/* Input Kode Obat */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="text"
            className="form-control form-control-sm"
            style={{
              width: 110,
              fontWeight: 700,
              background: '#ffffff',
              color: '#0369a1',
              textAlign: 'center',
              borderRadius: 6
            }}
            placeholder="Kode Obat"
            value={txtkdobat}
            onChange={(e) => setTxtkdobat(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchObatRules(txtkdobat);
              }
            }}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setShowSearchModal(true);
              handleSearchObat(txtkdobat);
            }}
            style={{ borderRadius: 6, padding: '4px 9px', fontSize: '0.75rem' }}
            title="Cari Obat di TB001"
          >
            <i className="bi bi-search" />
          </Button>
        </div>

        {/* Nama Obat */}
        <input
          type="text"
          readOnly
          className="form-control form-control-sm"
          style={{
            flex: 1,
            minWidth: 200,
            fontWeight: 700,
            background: '#f8fafc',
            color: '#0f172a',
            borderRadius: 6
          }}
          placeholder="Nama Obat"
          value={obatInfo.nama || ''}
        />

        {/* Satuan Beli & Isi */}
        <div
          style={{
            background: '#ffffff',
            padding: '4px 12px',
            borderRadius: 6,
            fontSize: '0.75rem',
            fontWeight: 700,
            color: '#0f172a',
            border: '1px solid #cbd5e1'
          }}
        >
          {obatInfo.satuanbeli || '-'}{obatInfo.xisiobat > 1 ? ` | ${obatInfo.xisiobat}` : ''}
        </div>

        {/* Satuan Jual */}
        <div
          style={{
            background: '#ffffff',
            padding: '4px 12px',
            borderRadius: 6,
            fontSize: '0.75rem',
            fontWeight: 700,
            color: '#0369a1',
            border: '1px solid #cbd5e1'
          }}
        >
          {obatInfo.satuan || '-'}
        </div>

        {isLoading && (
          <div className="d-flex align-items-center text-white" style={{ fontSize: '0.75rem', gap: 6 }}>
            <Spinner animation="border" size="sm" />
            <span>Sinkronisasi Aturan...</span>
          </div>
        )}
      </div>

      {/* 3. INPUT FORM / EDIT BARIS TERPILIH */}
      <div
        style={{
          background: '#e2e8f0',
          padding: '8px 18px',
          borderBottom: '1px solid #cbd5e1',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          fontSize: '0.72rem'
        }}
      >
        {/* Klasifikasi Info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 180 }}>
          <input
            type="text"
            readOnly
            className="form-control form-control-sm text-center"
            style={{ width: 45, fontWeight: 700, background: '#fef08a', color: '#854d0e', borderRadius: 4 }}
            value={formRow.kd_klasifikasi || ''}
            placeholder="KD"
          />
          <input
            type="text"
            readOnly
            className="form-control form-control-sm"
            style={{ width: 140, fontWeight: 700, background: '#ffffff', color: '#0f172a', borderRadius: 4 }}
            value={formRow.nm_klasifikasi || ''}
            placeholder="Pilih Klasifikasi"
          />
        </div>

        {/* Standar Hitung Selisih */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#475569' }}>SELISIH</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 70, fontSize: '0.72rem', fontWeight: 600, padding: '2px 5px', borderRadius: 4 }}
            value={formRow.selisih}
            onChange={(e) => handleFormChange('selisih', e.target.value)}
          >
            <option value="Tidak">Tidak</option>
            <option value="Ya">Ya</option>
          </select>
        </div>

        {/* Titip Jual */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#dc2626' }}>TITIP</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 70, fontSize: '0.72rem', fontWeight: 700, color: formRow.titip === 'Ya' ? '#dc2626' : '#334155', padding: '2px 5px', borderRadius: 4 }}
            value={formRow.titip}
            onChange={(e) => handleFormChange('titip', e.target.value)}
          >
            <option value="Tidak">Tidak</option>
            <option value="Ya">Ya</option>
          </select>
        </div>

        {/* Obat Khusus */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#ea580c' }}>KHUSUS</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 70, fontSize: '0.72rem', fontWeight: 700, color: formRow.khusus === 'Ya' ? '#ea580c' : '#334155', padding: '2px 5px', borderRadius: 4 }}
            value={formRow.khusus}
            onChange={(e) => handleFormChange('khusus', e.target.value)}
          >
            <option value="Tidak">Tidak</option>
            <option value="Ya">Ya</option>
          </select>
        </div>

        {/* Boleh Dijual */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#0369a1' }}>JUAL</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 70, fontSize: '0.72rem', fontWeight: 700, color: formRow.jual === 'Ya' ? '#16a34a' : '#64748b', padding: '2px 5px', borderRadius: 4 }}
            value={formRow.jual}
            onChange={(e) => handleFormChange('jual', e.target.value)}
          >
            <option value="Tidak">Tidak</option>
            <option value="Ya">Ya</option>
          </select>
        </div>

        {/* Dasar Jual */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#475569' }}>DASAR</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 80, fontSize: '0.72rem', fontWeight: 700, padding: '2px 5px', borderRadius: 4 }}
            value={formRow.acuanhrg}
            onChange={(e) => handleFormChange('acuanhrg', e.target.value)}
          >
            <option value="Sesuai">Sesuai</option>
            <option value="Khusus">Khusus</option>
          </select>
        </div>

        {/* Price List HNA */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#475569' }}>PRICELIST</span>
          <input
            type="text"
            readOnly
            className="form-control form-control-sm text-end"
            style={{ width: 75, fontSize: '0.72rem', fontWeight: 700, background: '#f8fafc', borderRadius: 4 }}
            value={fmtNum(formRow.hna)}
          />
        </div>

        {/* Disc */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#475569' }}>DISC [Rp]</span>
          <input
            type="number"
            className="form-control form-control-sm text-end"
            style={{ width: 65, fontSize: '0.72rem', fontWeight: 700, borderRadius: 4 }}
            value={formRow.disc}
            onChange={(e) => handleFormChange('disc', e.target.value)}
          />
        </div>

        {/* Harga Jadi */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#0369a1' }}>HRG JADI</span>
          <input
            type="text"
            readOnly
            className="form-control form-control-sm text-end"
            style={{ width: 75, fontSize: '0.72rem', fontWeight: 800, background: '#e0f2fe', color: '#0369a1', borderRadius: 4 }}
            value={fmtNum(formRow.harga)}
          />
        </div>

        {/* Normal: H.N & H.D */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#16a34a' }}>H.N. NORMAL</span>
          <input
            type="number"
            className="form-control form-control-sm text-end"
            style={{ width: 75, fontSize: '0.72rem', fontWeight: 700, borderRadius: 4 }}
            value={formRow.hna2}
            onChange={(e) => handleFormChange('hna2', e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#16a34a' }}>H.D. NORMAL</span>
          <input
            type="text"
            readOnly
            className="form-control form-control-sm text-end"
            style={{ width: 75, fontSize: '0.72rem', fontWeight: 700, background: '#f8fafc', borderRadius: 4 }}
            value={fmtNum(formRow.hda)}
          />
        </div>

        {/* Paket: H.N & H.D */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#7c3aed' }}>H.N. PAKET</span>
          <input
            type="number"
            className="form-control form-control-sm text-end"
            style={{ width: 75, fontSize: '0.72rem', fontWeight: 700, borderRadius: 4 }}
            value={formRow.hnapaket}
            onChange={(e) => handleFormChange('hnapaket', e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#7c3aed' }}>H.D. PAKET</span>
          <input
            type="text"
            readOnly
            className="form-control form-control-sm text-end"
            style={{ width: 75, fontSize: '0.72rem', fontWeight: 700, background: '#f8fafc', borderRadius: 4 }}
            value={fmtNum(formRow.hda2)}
          />
        </div>

        {/* Selisih Normal & Paket */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#b91c1c' }}>SELISIH N</span>
          <input
            type="text"
            readOnly
            className="form-control form-control-sm text-end"
            style={{
              width: 60,
              fontSize: '0.72rem',
              fontWeight: 800,
              color: formRow.laba < 0 ? '#dc2626' : '#16a34a',
              background: '#f8fafc',
              borderRadius: 4
            }}
            value={fmtNum(formRow.laba)}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#b91c1c' }}>SELISIH P</span>
          <input
            type="text"
            readOnly
            className="form-control form-control-sm text-end"
            style={{
              width: 60,
              fontSize: '0.72rem',
              fontWeight: 800,
              color: formRow.laba2 < 0 ? '#dc2626' : '#16a34a',
              background: '#f8fafc',
              borderRadius: 4
            }}
            value={fmtNum(formRow.laba2)}
          />
        </div>

        {/* Tombol Simpan */}
        <div style={{ display: 'flex', gap: 4, marginTop: 14 }}>
          <Button
            variant="success"
            size="sm"
            disabled={isSaving || !formRow.kd_klasifikasi}
            onClick={() => handleSimpanAturan()}
            style={{ fontWeight: 700, borderRadius: 5, padding: '3px 10px', fontSize: '0.72rem' }}
          >
            {isSaving ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-check2-circle me-1" /> Simpan</>}
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            disabled={isSaving || !obatInfo.kdbr}
            onClick={handleSetSemuaSesuai}
            style={{ fontWeight: 600, borderRadius: 5, padding: '3px 8px', fontSize: '0.7rem' }}
            title="Set semua klasifikasi menjadi 'Sesuai'"
          >
            Set Sesuai
          </Button>
        </div>
      </div>

      {/* 4. TABEL UTAMA ATURAN JUAL */}
      <div style={{ flex: 1, padding: '10px 18px', overflowY: 'auto' }}>
        <div
          style={{
            background: '#ffffff',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header Bar Tabel dengan Filter dan Pilihan Page Size */}
          <div
            style={{
              padding: '10px 16px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 10,
              fontSize: '0.75rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: '#e0f2fe',
                  color: '#0284c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.85rem'
                }}
              >
                <i className="bi bi-table" />
              </div>
              <div>
                <div className="fw-bold text-dark" style={{ fontSize: '0.8rem', lineHeight: '1.2' }}>
                  Daftar Klasifikasi Tarif &amp; Aturan Penjualan
                </div>
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>
                  Klik salah satu baris klasifikasi untuk mengubah aturan harga di panel bawah.
                </div>
              </div>
              <span
                className="badge rounded-pill ms-2"
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  padding: '3px 8px'
                }}
              >
                {filteredRules.length} Klasifikasi
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Filter Cepat Klasifikasi */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div className="input-group input-group-sm" style={{ width: 190 }}>
                  <span
                    className="input-group-text bg-white border-end-0 text-muted"
                    style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                  >
                    <i className="bi bi-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm border-start-0"
                    style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: '0 4px 4px 0' }}
                    placeholder="Cari klasifikasi..."
                    value={filterKlasifikasi}
                    onChange={(e) => {
                      setFilterKlasifikasi(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              {/* Baris per Halaman */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                  <i className="bi bi-list-ol me-1" />Baris:
                </span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 80, fontSize: '0.72rem', padding: '2px 6px', borderRadius: 5, borderColor: '#cbd5e1' }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5 Baris</option>
                  <option value={10}>10 Baris</option>
                  <option value={15}>15 Baris</option>
                  <option value={20}>20 Baris</option>
                  <option value="all">Semua</option>
                </select>
              </div>
            </div>
          </div>

          <Table
            bordered
            hover
            responsive
            className="mb-0 align-middle"
            style={{ fontSize: '0.72rem', borderCollapse: 'separate', borderSpacing: 0 }}
          >
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
              {/* Baris 1: Header Grup Utama */}
              <tr style={{ textAlign: 'center', color: '#334155', fontWeight: 700, fontSize: '0.67rem', letterSpacing: '0.02em' }}>
                <th
                  rowSpan="2"
                  className="align-middle text-center"
                  style={{ background: '#f1f5f9', width: 46, borderBottom: '2px solid #cbd5e1', borderColor: '#cbd5e1', color: '#475569' }}
                >
                  KD
                </th>
                <th
                  rowSpan="2"
                  className="align-middle text-start px-3"
                  style={{ background: '#f1f5f9', minWidth: 170, borderBottom: '2px solid #cbd5e1', borderColor: '#cbd5e1', color: '#1e293b' }}
                >
                  <div className="d-flex align-items-center gap-1">
                    <i className="bi bi-layers-fill text-secondary" style={{ fontSize: '0.72rem' }} />
                    <span>KLASIFIKASI TARIF</span>
                  </div>
                </th>
                <th
                  rowSpan="2"
                  className="align-middle text-center"
                  title="Standar Hitung Selisih"
                  style={{ background: '#f1f5f9', width: 70, borderBottom: '2px solid #cbd5e1', borderColor: '#cbd5e1', color: '#475569' }}
                >
                  HITUNG SELISIH
                </th>
                <th
                  rowSpan="2"
                  className="align-middle text-center"
                  title="Status Titip Jual"
                  style={{ background: '#f8fafc', width: 62, borderBottom: '2px solid #cbd5e1', borderColor: '#cbd5e1' }}
                >
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <span className="badge rounded-pill bg-light text-secondary border mb-1" style={{ fontSize: '0.58rem', padding: '1px 5px' }}>Titip</span>
                    <span style={{ fontSize: '0.66rem', color: '#475569', fontWeight: 700 }}>JUAL</span>
                  </div>
                </th>
                <th
                  rowSpan="2"
                  className="align-middle text-center"
                  title="Status Obat Khusus"
                  style={{ background: '#f8fafc', width: 62, borderBottom: '2px solid #cbd5e1', borderColor: '#cbd5e1' }}
                >
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <span className="badge rounded-pill bg-light text-secondary border mb-1" style={{ fontSize: '0.58rem', padding: '1px 5px' }}>Khusus</span>
                    <span style={{ fontSize: '0.66rem', color: '#475569', fontWeight: 700 }}>OBAT</span>
                  </div>
                </th>
                <th
                  rowSpan="2"
                  className="align-middle text-center"
                  title="Boleh Dijual ke Pasien"
                  style={{ background: '#f8fafc', width: 62, borderBottom: '2px solid #cbd5e1', borderColor: '#cbd5e1' }}
                >
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <span className="badge rounded-pill bg-light text-secondary border mb-1" style={{ fontSize: '0.58rem', padding: '1px 5px' }}>Boleh</span>
                    <span style={{ fontSize: '0.66rem', color: '#475569', fontWeight: 700 }}>DIJUAL</span>
                  </div>
                </th>
                <th
                  colSpan="2"
                  className="align-middle text-center py-1"
                  style={{ background: '#e2e8f0', color: '#1e293b', borderBottom: '1px solid #cbd5e1', borderColor: '#cbd5e1' }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-1">
                    <i className="bi bi-tag-fill text-muted" style={{ fontSize: '0.68rem' }} />
                    <span style={{ letterSpacing: '0.04em' }}>PRICE LIST</span>
                  </div>
                </th>
                <th
                  rowSpan="2"
                  className="align-middle text-center"
                  style={{ background: '#f1f5f9', width: 75, borderBottom: '2px solid #cbd5e1', borderColor: '#cbd5e1', color: '#475569' }}
                >
                  <div className="d-flex flex-column align-items-center">
                    <span>DISKON</span>
                    <span style={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 500 }}>(Rp)</span>
                  </div>
                </th>
                <th
                  rowSpan="2"
                  className="align-middle text-center"
                  style={{ background: '#e2e8f0', width: 88, borderBottom: '2px solid #cbd5e1', borderColor: '#cbd5e1' }}
                >
                  <div className="d-flex flex-column align-items-center">
                    <span style={{ color: '#0f172a', fontWeight: 800 }}>HARGA JADI</span>
                    <span style={{ fontSize: '0.58rem', color: '#475569', fontWeight: 600 }}>(Rp)</span>
                  </div>
                </th>
                <th
                  colSpan="4"
                  className="align-middle text-center py-1"
                  style={{ background: '#e2e8f0', color: '#1e293b', borderBottom: '1px solid #cbd5e1', borderColor: '#cbd5e1' }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-1">
                    <i className="bi bi-calculator text-muted" style={{ fontSize: '0.68rem' }} />
                    <span style={{ letterSpacing: '0.04em' }}>ATURAN HARGA JUAL</span>
                    <span style={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 500 }}>(Rp)</span>
                  </div>
                </th>
                <th
                  colSpan="2"
                  className="align-middle text-center py-1"
                  style={{ background: '#f1f5f9', color: '#334155', borderBottom: '1px solid #cbd5e1', borderColor: '#cbd5e1' }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-1">
                    <i className="bi bi-graph-up-arrow text-muted" style={{ fontSize: '0.68rem' }} />
                    <span style={{ letterSpacing: '0.04em' }}>SELISIH / MARGIN</span>
                    <span style={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 500 }}>(Rp)</span>
                  </div>
                </th>
              </tr>

              {/* Baris 2: Sub-Header Kolom Rincian */}
              <tr style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.64rem', letterSpacing: '0.02em' }}>
                <th
                  style={{ background: '#f8fafc', color: '#334155', width: 75, borderColor: '#cbd5e1', borderBottom: '2px solid #cbd5e1' }}
                  title="Dasar Pengambilan Acuan Harga"
                >
                  DASAR JUAL
                </th>
                <th
                  style={{ background: '#f8fafc', color: '#334155', width: 80, borderColor: '#cbd5e1', borderBottom: '2px solid #cbd5e1' }}
                  title="Nilai HNA Price List (Rp)"
                >
                  HNA (Rp)
                </th>
                <th
                  style={{ background: '#f8fafc', color: '#334155', width: 76, borderColor: '#cbd5e1', borderBottom: '2px solid #cbd5e1' }}
                  title="Harga Netto Normal (Pokok)"
                >
                  NORMAL H.N.
                </th>
                <th
                  style={{ background: '#f8fafc', color: '#334155', width: 76, borderColor: '#cbd5e1', borderBottom: '2px solid #cbd5e1' }}
                  title="Harga Dasar Normal"
                >
                  NORMAL H.D.
                </th>
                <th
                  style={{ background: '#f8fafc', color: '#334155', width: 76, borderColor: '#cbd5e1', borderBottom: '2px solid #cbd5e1' }}
                  title="Harga Netto Paket"
                >
                  PAKET H.N.
                </th>
                <th
                  style={{ background: '#f8fafc', color: '#334155', width: 76, borderColor: '#cbd5e1', borderBottom: '2px solid #cbd5e1' }}
                  title="Harga Dasar Paket"
                >
                  PAKET H.D.
                </th>
                <th
                  style={{ background: '#f8fafc', color: '#334155', width: 72, borderColor: '#cbd5e1', borderBottom: '2px solid #cbd5e1' }}
                  title="Margin Selisih Normal"
                >
                  NORMAL
                </th>
                <th
                  style={{ background: '#f8fafc', color: '#334155', width: 72, borderColor: '#cbd5e1', borderBottom: '2px solid #cbd5e1' }}
                  title="Margin Selisih Paket"
                >
                  PAKET
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRules.length === 0 ? (
                <tr>
                  <td colSpan="16" className="text-center py-5 text-muted">
                    {isLoading ? (
                      <><Spinner animation="border" size="sm" className="me-2 text-primary" /> Sedang memuat data aturan...</>
                    ) : !txtkdobat ? (
                      <div className="d-flex flex-column align-items-center justify-content-center py-3">
                        <i className="bi bi-capsule text-muted mb-2" style={{ fontSize: '1.8rem', opacity: 0.4 }} />
                        <span className="fw-bold text-dark mb-1" style={{ fontSize: '0.85rem' }}>Belum Ada Obat Terpilih</span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Silakan masukkan Kode Obat atau klik tombol <strong>Cari Obat</strong> di atas untuk memuat aturan penjualan.</span>
                      </div>
                    ) : (
                      'Belum ada data aturan penjualan untuk obat ini.'
                    )}
                  </td>
                </tr>
              ) : (
                paginatedRules.map((r, idx) => {
                  const isSelected = selectedRule === r.kd_klasifikasi;
                  const labaMin = (parseFloat(r.laba2 || 0) < 0) || (parseFloat(r.laba || 0) < 0);

                  return (
                    <tr
                      key={r.kd_klasifikasi || idx}
                      onClick={() => selectRule(r)}
                      style={{
                        cursor: 'pointer',
                        background: isSelected ? '#eff6ff' : (idx % 2 === 1 ? '#f8fafc' : '#ffffff'),
                        boxShadow: isSelected ? 'inset 3px 0 0 #0284c7' : 'none',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <td className="text-center font-monospace fw-semibold">{r.kd_klasifikasi}</td>
                      <td className="px-2">
                        <span className={isSelected ? 'fw-bold text-primary' : 'text-dark'}>
                          {r.nm_klasifikasi}
                        </span>
                        {isSelected && (
                          <Badge bg="primary" className="ms-1 px-1 py-0 shadow-xs" style={{ fontSize: '0.58rem' }}>
                            Dipilih
                          </Badge>
                        )}
                      </td>
                      <td className="text-center text-muted">
                        {r.selisih === 'Ya' ? <span className="fw-semibold text-primary">Ya</span> : 'Tidak'}
                      </td>
                      <td className="text-center">
                        {r.titip === 'Ya' ? (
                          <Badge bg="danger" className="px-1 py-0 fw-semibold" style={{ fontSize: '0.62rem' }}>Ya</Badge>
                        ) : (
                          <span className="text-muted" style={{ opacity: 0.4 }}>-</span>
                        )}
                      </td>
                      <td className="text-center">
                        {r.khusus === 'Ya' ? (
                          <Badge bg="warning" text="dark" className="px-1 py-0 fw-semibold" style={{ fontSize: '0.62rem' }}>Ya</Badge>
                        ) : (
                          <span className="text-muted" style={{ opacity: 0.4 }}>-</span>
                        )}
                      </td>
                      <td className="text-center">
                        {r.jual === 'Ya' ? (
                          <Badge bg="success" className="px-1 py-0 fw-semibold" style={{ fontSize: '0.62rem' }}>Ya</Badge>
                        ) : (
                          <Badge bg="secondary" className="px-1 py-0 fw-semibold" style={{ fontSize: '0.62rem' }}>Tidak</Badge>
                        )}
                      </td>
                      <td className="text-center text-muted">{r.acuanhrg || 'Sesuai'}</td>
                      <td className="text-end font-monospace">{fmtNum(r.hna)}</td>
                      <td className="text-end font-monospace">{fmtNum(r.disc)}</td>
                      <td className="text-end font-monospace fw-bold" style={{ color: '#0284c7' }}>{fmtNum(r.harga)}</td>
                      <td className="text-end font-monospace">{fmtNum(r.hna2)}</td>
                      <td className="text-end font-monospace">{fmtNum(r.hda)}</td>
                      <td className="text-end font-monospace">{fmtNum(r.hnapaket)}</td>
                      <td className="text-end font-monospace">{fmtNum(r.hda2)}</td>
                      <td className="text-end font-monospace fw-semibold" style={{ color: parseFloat(r.laba || 0) < 0 ? '#dc2626' : 'inherit' }}>
                        {parseFloat(r.laba || 0) < 0 ? (
                          <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-1 py-0 font-monospace">
                            {fmtNum(r.laba)}
                          </span>
                        ) : (
                          fmtNum(r.laba)
                        )}
                      </td>
                      <td className="text-end font-monospace fw-semibold" style={{ color: labaMin ? '#dc2626' : 'inherit' }}>
                        {parseFloat(r.laba2 || 0) < 0 ? (
                          <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-1 py-0 font-monospace">
                            {fmtNum(r.laba2)}
                          </span>
                        ) : (
                          fmtNum(r.laba2)
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>

          {/* Pagination Controls Bar Bawah Tabel */}
          <div
            style={{
              padding: '8px 14px',
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
              fontSize: '0.72rem'
            }}
          >
            <div className="text-muted">
              {filteredRules.length === 0 ? (
                'Menampilkan 0 data'
              ) : (
                <>
                  Menampilkan{' '}
                  <b>
                    {pageSize === 'all'
                      ? `1 - ${filteredRules.length}`
                      : `${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, filteredRules.length)}`}
                  </b>{' '}
                  dari <b>{filteredRules.length}</b> klasifikasi
                </>
              )}
            </div>

            {pageSize !== 'all' && totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 4 }}
                  title="Halaman Pertama"
                >
                  &laquo;
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 4 }}
                  title="Sebelumnya"
                >
                  &lsaquo;
                </Button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .map((p, i, arr) => {
                    const prevP = arr[i - 1];
                    const showEllipsis = prevP && p - prevP > 1;
                    return (
                      <React.Fragment key={p}>
                        {showEllipsis && <span className="px-1 text-muted">...</span>}
                        <Button
                          variant={currentPage === p ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => setCurrentPage(p)}
                          style={{
                            fontSize: '0.68rem',
                            padding: '2px 7px',
                            borderRadius: 4,
                            fontWeight: currentPage === p ? 700 : 500
                          }}
                        >
                          {p}
                        </Button>
                      </React.Fragment>
                    );
                  })}

                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 4 }}
                  title="Berikutnya"
                >
                  &rsaquo;
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 4 }}
                  title="Halaman Terakhir"
                >
                  &raquo;
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 5. BOTTOM STATUS & ACTION BAR */}
      <div
        style={{
          background: '#ffffff',
          borderTop: '1px solid #cbd5e1',
          padding: '8px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          boxShadow: '0 -2px 6px rgba(0,0,0,0.03)'
        }}
      >
        {/* Status Message */}
        <div style={{ flex: 1, minWidth: 260 }}>
          <div
            style={{
              fontSize: '0.72rem',
              color: statusMsg.type === 'danger' ? '#dc2626' : (statusMsg.type === 'success' ? '#16a34a' : '#334155'),
              fontWeight: 600
            }}
          >
            <i className={`bi ${statusMsg.type === 'danger' ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'} me-1`} />
            {statusMsg.text}
          </div>
        </div>

        {/* Buttons Action */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleOpenModal('pembagian')}
            style={{ fontWeight: 700, borderRadius: 6, fontSize: '0.72rem', padding: '4px 10px' }}
          >
            <i className="bi bi-pie-chart me-1" /> PEMBAGIAN OBAT KHUSUS
          </Button>

          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => handleOpenModal('khusus')}
            style={{ fontWeight: 700, borderRadius: 6, fontSize: '0.72rem', padding: '4px 10px' }}
          >
            <i className="bi bi-printer me-1" /> DAFTAR OBAT KHUSUS
          </Button>

          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => handleOpenModal('titip')}
            style={{ fontWeight: 700, borderRadius: 6, fontSize: '0.72rem', padding: '4px 10px' }}
          >
            <i className="bi bi-printer me-1" /> DAFTAR TITIP
          </Button>

          {/* Dropdown Laporan */}
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              style={{ fontWeight: 700, borderRadius: 6, fontSize: '0.72rem', padding: '4px 10px' }}
            >
              <i className="bi bi-journal-text me-1" /> LAPORAN
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ fontSize: '0.75rem' }}>
              <Dropdown.Item onClick={() => handleOpenModal('laba_minus')}>
                <i className="bi bi-graph-down-arrow text-danger me-2" /> Laporan Laba Minus
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleOpenModal('ditutup')}>
                <i className="bi bi-x-circle text-warning me-2" /> Stok Ada Aturan Ditutup
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button
            variant="danger"
            size="sm"
            onClick={handleBersih}
            style={{ fontWeight: 700, borderRadius: 6, fontSize: '0.72rem', padding: '4px 12px' }}
          >
            <i className="bi bi-x-octagon me-1" /> Bersih
          </Button>
        </div>
      </div>

      {/* ============================================================ */}
      {/* MODAL 1: PENCARIAN OBAT (TB001) */}
      {/* ============================================================ */}
      <Modal show={showSearchModal} onHide={() => setShowSearchModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: '#0e7490', color: '#fff', padding: '10px 16px' }}>
          <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 800 }}>
            <i className="bi bi-search me-2" /> Pencarian Master Obat (TB001)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px 16px' }}>
          <div className="d-flex gap-2 mb-3">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Ketik Nama Obat atau Kode Obat..."
              value={searchKeyword}
              autoFocus
              onChange={(e) => {
                setSearchKeyword(e.target.value);
                handleSearchObat(e.target.value);
              }}
            />
            <Button variant="primary" size="sm" onClick={() => handleSearchObat(searchKeyword)}>
              Cari
            </Button>
          </div>

          <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
            <Table hover bordered size="sm" style={{ fontSize: '0.72rem' }}>
              <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ width: 85 }}>Kode</th>
                  <th>Nama Obat</th>
                  <th style={{ width: 80 }}>Satuan</th>
                  <th style={{ width: 90 }} className="text-end">Harga Pokok</th>
                  <th style={{ width: 120 }}>Pabrik / Prinsipal</th>
                  <th style={{ width: 65 }} className="text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isSearching ? (
                  <tr>
                    <td colSpan="6" className="text-center py-3 text-muted">
                      <Spinner animation="border" size="sm" className="me-2" /> Mencari data obat...
                    </td>
                  </tr>
                ) : searchResults.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-3 text-muted">
                      Tidak ada obat ditemukan.
                    </td>
                  </tr>
                ) : (
                  searchResults.map((item) => (
                    <tr key={item.kdbr} style={{ cursor: 'pointer' }} onClick={() => handlePilihObatModal(item)}>
                      <td className="fw-bold text-primary">{item.kdbr}</td>
                      <td>{item.nama}</td>
                      <td>{item.satuan || '-'}</td>
                      <td className="text-end fw-bold">{fmtNum(item.harga)}</td>
                      <td>{item.pabrik || '-'}</td>
                      <td className="text-center">
                        <Button size="sm" variant="success" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                          Pilih
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* ============================================================ */}
      {/* MODAL 2: LAPORAN-LAPORAN & PEMBAGIAN KHUSUS */}
      {/* ============================================================ */}
      <Modal show={modalType !== null} onHide={() => setModalType(null)} size="xl" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#fff', padding: '12px 18px' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: 700 }} className="d-flex align-items-center gap-2">
            <i
              className={`bi ${
                modalType === 'laba_minus'
                  ? 'bi-graph-down-arrow text-danger'
                  : modalType === 'titip'
                  ? 'bi-tags-fill text-warning'
                  : modalType === 'khusus'
                  ? 'bi-stars text-info'
                  : modalType === 'pembagian'
                  ? 'bi-pie-chart-fill text-success'
                  : 'bi-slash-circle text-muted'
              }`}
            />
            <span>{modalTitle}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {isLoadingModal ? (
            <div className="text-center py-4 text-muted">
              <Spinner animation="border" size="sm" className="me-2" /> Memuat data laporan...
            </div>
          ) : modalType === 'pembagian' ? (
            <div>
              <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-info-circle me-1" /> Atur persentase bagi hasil obat khusus untuk setiap klasifikasi tarif. Pastikan total persentase sesuai regulasi.
              </div>
              <Table bordered hover size="sm" style={{ fontSize: '0.72rem' }}>
                <thead style={{ background: '#0f172a', color: '#f8fafc', fontSize: '0.68rem', letterSpacing: '0.02em' }}>
                  <tr className="text-center">
                    <th style={{ width: 60 }}>KD</th>
                    <th>Klasifikasi Tarif</th>
                    <th style={{ width: 85 }}>Dokter [%]</th>
                    <th style={{ width: 85 }}>Seminar [%]</th>
                    <th style={{ width: 85 }}>Penulis [%]</th>
                    <th style={{ width: 85 }}>BU [%]</th>
                    <th style={{ width: 85 }}>Pemilik [%]</th>
                    <th style={{ width: 85 }}>Nilai [%]</th>
                  </tr>
                </thead>
                <tbody>
                  {modalData.map((item, idx) => (
                    <tr key={item.kd_klasifikasi}>
                      <td className="text-center fw-bold">{item.kd_klasifikasi}</td>
                      <td>{item.nm_klasifikasi}</td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          style={{ fontSize: '0.72rem' }}
                          value={item.dokter || 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setModalData((prev) =>
                              prev.map((r, i) => (i === idx ? { ...r, dokter: val } : r))
                            );
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          style={{ fontSize: '0.72rem' }}
                          value={item.seminar || 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setModalData((prev) =>
                              prev.map((r, i) => (i === idx ? { ...r, seminar: val } : r))
                            );
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          style={{ fontSize: '0.72rem' }}
                          value={item.penulis || 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setModalData((prev) =>
                              prev.map((r, i) => (i === idx ? { ...r, penulis: val } : r))
                            );
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          style={{ fontSize: '0.72rem' }}
                          value={item.bu || 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setModalData((prev) =>
                              prev.map((r, i) => (i === idx ? { ...r, bu: val } : r))
                            );
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          style={{ fontSize: '0.72rem' }}
                          value={item.pemilik || 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setModalData((prev) =>
                              prev.map((r, i) => (i === idx ? { ...r, pemilik: val } : r))
                            );
                          }}
                        />
                      </td>
                      <td className="text-center fw-bold">{item.nilai || 100}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div>
              {/* Header Modal Bar: Search & Page Size & PDF Button */}
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ width: 160, fontSize: '0.72rem' }}
                    placeholder="Filter laporan..."
                    value={modalSearch}
                    onChange={(e) => {
                      setModalSearch(e.target.value);
                      setModalPage(1);
                    }}
                  />
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 75, fontSize: '0.72rem' }}
                    value={modalPageSize}
                    onChange={(e) => {
                      setModalPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10));
                      setModalPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value="all">Semua</option>
                  </select>
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                    Total: <b>{filteredModalData.length}</b> data
                  </span>
                </div>

                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    const url = `${apiUrl}/aturanjual_cetak_pdf?jenis=${modalType}`;
                    window.open(url, '_blank');
                  }}
                  style={{ fontSize: '0.72rem', fontWeight: 700 }}
                >
                  <i className="bi bi-file-earmark-pdf me-1" /> Cetak PDF
                </Button>
              </div>

              <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                <Table bordered hover responsive size="sm" style={{ fontSize: '0.7rem' }}>
                  <thead style={{ background: '#0f172a', color: '#f8fafc', position: 'sticky', top: 0, zIndex: 5, fontSize: '0.68rem', letterSpacing: '0.02em' }}>
                    <tr>
                      <th style={{ width: 40 }} className="text-center">No</th>
                      <th style={{ width: 75 }}>Kode</th>
                      <th>Nama Obat</th>
                      {modalType === 'titip' || modalType === 'khusus' ? <th>Prinsipal</th> : null}
                      <th>Klasifikasi</th>
                      {modalType === 'ditutup' ? (
                        <th style={{ width: 100 }} className="text-end">Saldo Stok</th>
                      ) : (
                        <>
                          <th style={{ width: 65 }} className="text-center">Boleh Jual</th>
                          <th style={{ width: 75 }} className="text-end">PriceList</th>
                          <th style={{ width: 75 }} className="text-end">HNA Normal</th>
                          <th style={{ width: 75 }} className="text-end">HDA Normal</th>
                          <th style={{ width: 75 }} className="text-end">HNA Paket</th>
                          <th style={{ width: 75 }} className="text-end">HDA Paket</th>
                          <th style={{ width: 70 }} className="text-end">Selisih N</th>
                          <th style={{ width: 70 }} className="text-end">Selisih P</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModalData.length === 0 ? (
                      <tr>
                        <td colSpan="14" className="text-center py-3 text-muted">
                          Tidak ada data untuk laporan ini.
                        </td>
                      </tr>
                    ) : (
                      paginatedModalData.map((row, idx) => {
                        const rowNum = modalPageSize === 'all' ? idx + 1 : (modalPage - 1) * modalPageSize + idx + 1;
                        return (
                          <tr key={idx}>
                            <td className="text-center">{rowNum}</td>
                            <td className="fw-bold">{row.kd_obat1 || row.kd_obat}</td>
                            <td>{row.nm_obat}</td>
                            {modalType === 'titip' || modalType === 'khusus' ? <td>{row.nm_prinsip || '-'}</td> : null}
                            <td>{row.nm_klasifikasi || '-'}</td>
                            {modalType === 'ditutup' ? (
                              <td className="text-end fw-bold">{fmtNum(row.saldo)}</td>
                            ) : (
                              <>
                                <td className="text-center">{row.jual || 'Tidak'}</td>
                                <td className="text-end">{fmtNum(row.hna)}</td>
                                <td className="text-end">{fmtNum(row.hna2)}</td>
                                <td className="text-end">{fmtNum(row.hda)}</td>
                                <td className="text-end">{fmtNum(row.hnapaket)}</td>
                                <td className="text-end">{fmtNum(row.hda2)}</td>
                                <td className="text-end fw-bold" style={{ color: parseFloat(row.laba || 0) < 0 ? '#dc2626' : 'inherit' }}>
                                  {fmtNum(row.laba)}
                                </td>
                                <td className="text-end fw-bold" style={{ color: parseFloat(row.laba2 || 0) < 0 ? '#dc2626' : 'inherit' }}>
                                  {fmtNum(row.laba2)}
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination Controls Modal */}
              {modalPageSize !== 'all' && totalModalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: '0.72rem' }}>
                  <span className="text-muted">
                    Halaman <b>{modalPage}</b> dari <b>{totalModalPages}</b>
                  </span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={modalPage === 1}
                      onClick={() => setModalPage(1)}
                      style={{ fontSize: '0.68rem', padding: '2px 7px' }}
                    >
                      &laquo;
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={modalPage === 1}
                      onClick={() => setModalPage((p) => Math.max(1, p - 1))}
                      style={{ fontSize: '0.68rem', padding: '2px 7px' }}
                    >
                      &lsaquo;
                    </Button>
                    <span className="px-2 py-1 bg-light border rounded fw-bold text-primary">
                      {modalPage} / {totalModalPages}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={modalPage === totalModalPages}
                      onClick={() => setModalPage((p) => Math.min(totalModalPages, p + 1))}
                      style={{ fontSize: '0.68rem', padding: '2px 7px' }}
                    >
                      &rsaquo;
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={modalPage === totalModalPages}
                      onClick={() => setModalPage(totalModalPages)}
                      style={{ fontSize: '0.68rem', padding: '2px 7px' }}
                    >
                      &raquo;
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 16px' }}>
          {modalType === 'pembagian' && (
            <Button variant="success" size="sm" onClick={handleSavePembagian} style={{ fontSize: '0.75rem', fontWeight: 700 }}>
              <i className="bi bi-check2-circle me-1" /> Simpan Perubahan
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => setModalType(null)} style={{ fontSize: '0.75rem' }}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AturanJual;
